import React, { useEffect, useMemo, useRef, useState } from 'react'

import { Row } from 'antd'
import _ from 'lodash'

import CustomInput from '../../../../../../components/CustomInput/customInput'
import CustomSelect from '../../../../../../components/CustomSelect/customSelect'
import CustomSwitch from '../../../../../../components/CustomSwitch/customSwitch'
import { getMessage } from '../../../../../../util/IntlMessages'
import { isAdmin } from '../../../../../../util/profiles'
import { actionStatus } from '../../constants'
import { useCreateActionContext } from '../../contexts/action'
import {
  useGetActionStatusQuery,
  useGetActionTypesQuery,
  useGetCustomersQuery,
  useGetHarvestsQuery,
  useGetLocationsQuery,
  useGetMembersQuery
} from '../../services/field'

export function BaseFields({ values, errors, handleBlur, setValues }) {
  const [formValues, setFormValues] = useState({})

  useEffect(
    function updateFormValues() {
      if (!Object.keys(formValues).length) {
        setFormValues(values)
      }

      if (formValues.actionStatusId !== values.actionStatusId) {
        setFormValues((prev) => ({ ...prev, actionStatusId: values.actionStatusId }))
      }
    },
    [values]
  )

  const debouncedSetValues = useRef(_.debounce(setValues, 200)).current

  useEffect(() => () => debouncedSetValues.cancel(), [debouncedSetValues])

  const { isNewAction, isPreview, isReadOnly, currentUser } = useCreateActionContext()

  function onChangeValue(fieldName, value) {
    let newValues = { ...formValues, [fieldName]: value }

    if (fieldName === 'actionTypeId') {
      newValues = _.omit(newValues, ['customerId', 'memberId', 'locationId'])
    }

    if (fieldName === 'customerId') {
      newValues = _.omit(newValues, ['memberId', 'locationId'])
    }

    if (fieldName === 'memberId') {
      newValues = _.omit(newValues, ['locationId'])
    }

    setFormValues(newValues)
    debouncedSetValues('baseFields', newValues)
  }

  const { data: actionTypesList } = useGetActionTypesQuery({
    ...(currentUser.parentMember && { memberIds: currentUser.parentMember.id })
  })

  const { data: actionStatusList } = useGetActionStatusQuery()

  const { data: harvestsList } = useGetHarvestsQuery()

  const customersQueryParams = { actionTypeId: formValues?.actionTypeId }
  const { data: customersList } = useGetCustomersQuery(customersQueryParams, { skip: !formValues?.actionTypeId })

  const membersQueryParams = { customerId: formValues?.customerId, currentUser }
  const { data: membersList } = useGetMembersQuery(membersQueryParams, { skip: !formValues?.customerId })

  const locationsQueryParams = { customerId: formValues?.customerId, memberId: formValues?.memberId, currentUser }
  const { data: locationsList } = useGetLocationsQuery(locationsQueryParams, { skip: !formValues?.customerId })

  const isDateRequired = useMemo(
    () =>
      [actionStatus.DONE, actionStatus.PLANNED].includes(
        actionStatusList?.find(({ value }) => value === formValues?.actionStatusId)?.description
      ),
    [actionStatusList, formValues?.actionStatusId]
  )

  const isSwitchVisible = isAdmin(currentUser)

  const disabledField = isReadOnly || isPreview

  return (
    <div>
      <Row type="flex">
        <CustomInput
          disabled={disabledField || formValues['actionTemplateItemId'] != null}
          field="actionSubject"
          required
          errors={errors}
          setFieldValue={onChangeValue}
          rowSpan={12}
          value={{ actionSubject: formValues['actionSubject'] }}
          handleBlur={handleBlur}
          id="actionsDescriptionIdKey01"
        />
        <CustomSelect
          showSearch
          enable={disabledField || formValues['actionTemplateItemId'] != null || !isNewAction}
          field="actionTypeId"
          errors={errors}
          required
          setFieldValue={onChangeValue}
          rowSpan={12}
          value={{ actionTypeId: formValues['actionTypeId'] }}
          values={actionTypesList}
          handleBlur={handleBlur}
          id="actionsSelection"
        />
      </Row>

      <Row type="flex" justify="start">
        <CustomSelect
          showSearch
          enable={disabledField}
          field="actionStatusId"
          errors={errors}
          required
          setFieldValue={onChangeValue}
          rowSpan={12}
          value={{ actionStatusId: formValues['actionStatusId'] }}
          values={actionStatusList}
          handleBlur={handleBlur}
          id="actionsSelectionIdKey02"
        />
        <CustomSelect
          showSearch
          enable={!formValues['actionTypeId'] || disabledField || formValues['actionTemplateItemId'] != null}
          field="customerId"
          errors={errors}
          required
          setFieldValue={onChangeValue}
          rowSpan={12}
          value={{ customerId: formValues['customerId'] }}
          values={customersList}
          handleBlur={handleBlur}
          id="actionsSelectionIdKey03"
        />
      </Row>

      <Row type="flex" justify="start">
        <CustomSelect
          showSearch
          enable={!formValues['customerId'] || disabledField}
          field="memberId"
          errors={errors}
          required
          setFieldValue={onChangeValue}
          rowSpan={12}
          value={{ memberId: formValues['memberId'] }}
          values={membersList}
          handleBlur={handleBlur}
          id="actionsSelectionIdKey04"
        />
        <CustomSelect
          showSearch
          enable={!formValues['customerId'] || disabledField}
          field="locationId"
          errors={errors}
          required
          setFieldValue={onChangeValue}
          rowSpan={12}
          value={{ locationId: formValues['locationId'] }}
          values={locationsList}
          handleBlur={handleBlur}
          id="actionsSelectionIdKey05"
        />
      </Row>

      <Row type="flex">
        <CustomSelect
          showSearch
          enable={disabledField}
          field="referencePeriodId"
          errors={errors}
          required
          setFieldValue={onChangeValue}
          rowSpan={6}
          value={{ referencePeriodId: formValues['referencePeriodId'] }}
          values={harvestsList}
          handleBlur={handleBlur}
          id="actionsSelectionIdKey06"
          className="ant-form-item"
        />
        <CustomInput
          type="date"
          disabled={disabledField}
          field="executionDate"
          errors={errors}
          setFieldValue={onChangeValue}
          rowSpan={6}
          value={{ executionDate: formValues['executionDate'] }}
          handleBlur={handleBlur}
          required={isDateRequired}
          id="actionsSelectionIdKey06"
        />
        {/*
            [Suporte #46638] Visitas - Remover campo "Hora" do formulário principal
            USER STORY 10613 (AzureDevops)
          */}
        {/* <CustomInput
            type="hour"
            disabled={disabledField}
            field="executionTime"
            errors={errors}
            setFieldValue={onChangeValue}
            rowSpan={6}
            value={{ executionTime: formValues['executionTime'] }}
            handleBlur={handleBlur}
            required={isDateRequired}
            id="actionsDate"
          /> */}
        {isSwitchVisible && (
          <CustomSwitch
            field="active"
            disabled={disabledField}
            errors={errors}
            setFieldValue={onChangeValue}
            rowSpan={8}
            value={{ active: formValues['active'] }}
            handleBlur={handleBlur}
            unCheckedChildren={getMessage('label.inactive')}
            checkedChildren={getMessage('label.active')}
            defaultValue={true}
          />
        )}
      </Row>
    </div>
  )
}
